import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, PencilSimple, Copy, Trash, LinkSimple, Eye, ArrowSquareOut, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/format";

const ISSUE_TYPES = ["Onboarding", "Offboarding", "General Issue"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminForms() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [forms, setForms] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", issue_type: "General Issue" });
  const load = () => api.get("/forms").then((r) => setForms(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      const r = await api.post("/forms", form);
      setCreateOpen(false);
      setForm({ title: "", issue_type: "General Issue" });
      nav(`/admin/forms/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const toggleActive = async (f) => {
    await api.patch(`/forms/${f.id}`, { is_active: !f.is_active });
    load();
  };

  const duplicate = async (f) => { await api.post(`/forms/${f.id}/duplicate`); load(); toast.success("Duplicated"); };
  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.title}"?`)) return;
    await api.delete(`/forms/${f.id}`); load(); toast.success("Deleted");
  };
  const copyLink = (f) => {
    const url = `${window.location.origin}/forms/${f.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const [previewForm, setPreviewForm] = useState(null);
  const openPreview = (f) => setPreviewForm(f);
  const closePreview = () => setPreviewForm(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Forms</h1>
          <p className="text-sm text-slate-500 mt-1">Custom intake forms — submissions land in your ticket queue.</p>
        </div>
        {isAdmin && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1" data-testid="new-form-btn"><Plus size={14} /> New form</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200 max-w-md">
              <DialogHeader><DialogTitle>New form</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-700">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-white border-slate-200" data-testid="form-title" />
                </div>
                <div>
                  <Label className="text-xs text-slate-700">Issue type</Label>
                  <Select value={form.issue_type} onValueChange={(v) => setForm({ ...form, issue_type: v })}>
                    <SelectTrigger className="mt-1 bg-white border-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {ISSUE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} className="bg-[#4f4c90] hover:bg-[#5F5CA5]" data-testid="form-create-submit">Create & edit</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="hairline rounded-lg bg-white overflow-hidden">
        {forms.length === 0 && <div className="p-10 text-center text-slate-500 text-sm">No forms yet.</div>}
        {forms.map((f) => (
          <FormRow
            key={f.id}
            f={f}
            isAdmin={isAdmin}
            onPreview={openPreview}
            onCopy={copyLink}
            onToggleActive={toggleActive}
            onDuplicate={duplicate}
            onRemove={remove}
          />
        ))}
      </div>

      {/* Preview dialog — renders the form as a requester would see it */}
      <Dialog open={!!previewForm} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Eye size={16} weight="duotone" className="text-[#4f4c90]" /> Preview: {previewForm?.title}
            </DialogTitle>
          </DialogHeader>
          {previewForm && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <span className="chip !text-[#4f4c90] bg-[#4f4c90]/10 !border-[#4f4c90]/30 !text-[10px]">{previewForm.issue_type}</span>
                {!previewForm.is_active && <span className="chip status-waiting !text-[10px]">Inactive — public link disabled</span>}
              </div>
              {previewForm.description && (
                <p className="text-sm text-slate-600 border-l-2 border-[#4f4c90]/40 pl-3 italic">{previewForm.description}</p>
              )}
              <FormFieldsPreview fields={previewForm.fields || []} />
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between flex-wrap">
            <div className="text-[11px] text-slate-500 font-mono">
              This is a read-only preview. Fields do not submit.
            </div>
            <div className="flex items-center gap-2">
              {previewForm?.is_active && (
                <a
                  href={`/forms/${previewForm.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-slate-600 hover:text-[#4f4c90] flex items-center gap-1 border border-slate-200 rounded-md px-2 py-1.5"
                  data-testid="form-preview-open-live"
                >
                  <ArrowSquareOut size={12} /> Open live
                </a>
              )}
              <Button
                onClick={() => { const id = previewForm.id; closePreview(); nav(`/admin/forms/${id}`); }}
                variant="outline"
                className="h-8 border-slate-200 bg-white text-xs gap-1"
                data-testid="form-preview-edit"
              >
                <PencilSimple size={12} /> Edit
              </Button>
              <Button
                onClick={closePreview}
                className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-8 text-xs"
                data-testid="form-preview-close"
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormFieldsPreview({ fields }) {
  // Always-visible standard requester fields at the top — same as PublicForm.
  return (
    <div className="space-y-3 opacity-90 pointer-events-none select-none">
      <div>
        <Label className="text-xs">Your name <span className="text-red-500">*</span></Label>
        <Input disabled placeholder="Alex Doe" className="mt-1 bg-slate-50 border-slate-200" />
      </div>
      <div>
        <Label className="text-xs">Your email <span className="text-red-500">*</span></Label>
        <Input disabled placeholder="alex@company.com" className="mt-1 bg-slate-50 border-slate-200" />
      </div>
      {fields.length === 0 && (
        <div className="text-[11px] text-slate-500 italic border border-dashed border-slate-200 rounded-md p-3">
          No custom fields yet. Add some in the form builder.
        </div>
      )}
      {fields.map((fld) => (
        <div key={fld.id}>
          <Label className="text-xs">{fld.label} {fld.required && <span className="text-red-500">*</span>}</Label>
          {fld.help_text && <p className="text-[11px] text-slate-500 mb-1">{fld.help_text}</p>}
          {fld.field_type === "short_text" && <Input disabled className="mt-1 bg-slate-50 border-slate-200" />}
          {fld.field_type === "long_text" && <Textarea disabled className="mt-1 bg-slate-50 border-slate-200 min-h-20" />}
          {fld.field_type === "email" && <Input disabled type="email" placeholder="name@example.com" className="mt-1 bg-slate-50 border-slate-200" />}
          {fld.field_type === "date" && <Input disabled type="date" className="mt-1 bg-slate-50 border-slate-200" />}
          {fld.field_type === "select" && (
            <Select disabled>
              <SelectTrigger className="mt-1 bg-slate-50 border-slate-200"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {(fld.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {fld.field_type === "multi_select" && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(fld.options || []).map((o) => (
                <span key={o} className="text-xs px-3 py-1.5 rounded-md border bg-slate-50 text-slate-600 border-slate-200">{o}</span>
              ))}
            </div>
          )}
          {fld.field_type === "checkbox" && (
            <label className="flex items-center gap-2 mt-1 text-sm text-slate-600">
              <Checkbox disabled checked={false} /> Yes
            </label>
          )}
        </div>
      ))}
    </div>
  );
}

// Individual form row with an expand-to-see-submissions dropdown.
function FormRow({ f, isAdmin, onPreview, onCopy, onToggleActive, onDuplicate, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const [subs, setSubs] = useState(null);   // null = not-loaded, [] = loaded empty
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const nav = useNavigate();

  const fetchSubs = async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get(`/forms/${f.id}/submissions`);
      setSubs(r.data || []);
    } catch {
      setSubs(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && subs === null && !loading) await fetchSubs();
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`form-row-${f.id}`}>
      <div className="flex items-center gap-2 px-4 py-3 row-hover">
        <button
          type="button"
          onClick={toggle}
          className="text-slate-400 hover:text-[#4f4c90] p-1.5 rounded-md hover:bg-slate-50 shrink-0"
          data-testid={`form-expand-${f.id}`}
          title={expanded ? "Hide submissions" : "Show submissions"}
        >
          <CaretRight size={14} weight="bold" className={`transition-transform duration-150 ${expanded ? "rotate-90" : ""}`} />
        </button>
        <button
          type="button"
          onClick={() => onPreview(f)}
          className="flex-1 min-w-0 text-left cursor-pointer"
          data-testid={`form-preview-${f.id}`}
          title="Click to preview this form"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium hover:text-[#4f4c90] transition-colors">{f.title}</span>
            <span className="chip !text-[#4f4c90] bg-[#4f4c90]/10 !border-[#4f4c90]/30">{f.issue_type}</span>
            {f.is_active ? <span className="chip status-in_progress">active</span> : <span className="chip status-waiting">inactive</span>}
          </div>
          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
            {f.fields.length} field{f.fields.length === 1 ? "" : "s"} · updated {timeAgo(f.updated_at)} · <span className="text-[#8B87C8]">/forms/{f.slug}</span>
          </div>
        </button>
        <button onClick={() => onPreview(f)} className="text-slate-500 hover:text-[#4f4c90] p-1.5 rounded-md hover:bg-slate-50" title="Preview"><Eye size={14} /></button>
        <button onClick={() => onCopy(f)} className="text-slate-500 hover:text-[#4f4c90] p-1.5 rounded-md hover:bg-slate-50" title="Copy link"><LinkSimple size={14} /></button>
        <Link to={`/admin/forms/${f.id}`} className="text-slate-500 hover:text-[#4f4c90] p-1.5 rounded-md hover:bg-slate-50" title="Edit"><PencilSimple size={14} /></Link>
        {isAdmin && <>
          <button onClick={() => onToggleActive(f)} className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1 rounded-md hover:bg-slate-50">{f.is_active ? "Deactivate" : "Activate"}</button>
          <button onClick={() => onDuplicate(f)} className="text-slate-500 hover:text-slate-900 p-1.5 rounded-md hover:bg-slate-50" title="Duplicate"><Copy size={14} /></button>
          <button onClick={() => onRemove(f)} className="text-red-500 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50" title="Delete"><Trash size={14} /></button>
        </>}
      </div>

      {expanded && (
        <div className="bg-slate-50/60 border-t border-slate-100 px-4 py-3" data-testid={`form-submissions-${f.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2 flex items-center gap-2">
            Submissions
            {subs !== null && (
              <span className="font-mono px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600 !text-[10px]">
                {subs.length}
              </span>
            )}
          </div>
          {loading && <div className="text-xs text-slate-500 italic">Loading…</div>}
          {!loading && error && (
            <div className="text-xs text-red-600 italic flex items-center gap-2">
              Failed to load submissions.
              <button
                type="button"
                onClick={fetchSubs}
                className="underline text-[#4f4c90] hover:text-[#5F5CA5]"
                data-testid={`form-submissions-retry-${f.id}`}
              >
                Retry
              </button>
            </div>
          )}
          {!loading && !error && subs !== null && subs.length === 0 && (
            <div className="text-xs text-slate-500 italic">No submissions yet — share <span className="font-mono text-slate-600">/forms/{f.slug}</span> to start collecting.</div>
          )}
          {!loading && subs && subs.length > 0 && (
            <div className="rounded-md bg-white border border-slate-200 divide-y divide-slate-100">
              {subs.map((s) => (
                <button
                  key={s.id}
                  onClick={() => s.ticket_id && nav(`/tickets/${s.ticket_id}`)}
                  disabled={!s.ticket_id}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid={`form-submission-row-${s.id}`}
                  title={s.ticket_id ? `Open ticket #${s.ticket_number}` : "No ticket associated"}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800 font-medium truncate">
                      {s.submitted_by_name || s.submitted_by_email}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">
                      {s.submitted_by_email} · {fmt(s.submitted_at)}
                    </div>
                  </div>
                  {s.ticket_number ? (
                    <span
                      className={`chip !text-[11px] !py-0.5 ${
                        s.ticket_deleted
                          ? "!text-slate-500 !border-slate-300 bg-slate-100"
                          : "!text-[#4f4c90] !border-[#4f4c90]/30 bg-[#4f4c90]/10"
                      }`}
                    >
                      #{s.ticket_number}{s.ticket_deleted ? " (deleted)" : ""}
                    </span>
                  ) : (
                    <span className="chip !text-slate-500 !border-slate-300 bg-slate-100 !text-[11px] !py-0.5">no ticket</span>
                  )}
                  {s.ticket_status && (
                    <span className={`chip !text-[11px] !py-0.5 status-${s.ticket_status}`}>
                      {s.ticket_status.replace(/_/g, " ")}
                    </span>
                  )}
                  <ArrowSquareOut size={12} className="text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
